import { useState, forwardRef, Fragment } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import HttpService from "../services/HttpService";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  CircularProgress,
  Container,
  Grid,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import OnboardingDetail from "../components/OnboardingDetail";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
// import EditLocationAltIcon from "@mui/icons-material/EditLocationAlt";
import DeleteIcon from "@mui/icons-material/Delete";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import RenderOnRole from "../components/RenderOnRole";
import Labels from "../labels/Labels";

export default function StartedOnboardings() {
  const columns = [
    {
      field: "id",
      headerName: Labels.getLabel("startedOnboardings.column.id", "ID"),
      width: 1,
      hide: true,
    },
    {
      field: "memberId",
      headerName: Labels.getLabel(
        "startedOnboardings.column.memberId",
        "Member Id"
      ),
      width: 100,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.memberId}
          </Typography>
        );
      },
    },
    {
      field: "companyId",
      headerName: Labels.getLabel(
        "startedOnboardings.column.companyId",
        "Company Id"
      ),
      width: 100,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.companyId}
          </Typography>
        );
      },
    },
    {
      field: "firstName",
      headerName: Labels.getLabel(
        "startedOnboardings.column.firstName",
        "First Name"
      ),
      width: 100,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.firstName}
          </Typography>
        );
      },
    },
    {
      field: "lastName",
      headerName: Labels.getLabel(
        "startedOnboardings.column.lastName",
        "Last Name"
      ),
      width: 100,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.lastName}
          </Typography>
        );
      },
    },
    {
      field: "email",
      headerName: Labels.getLabel("startedOnboardings.column.email", "Email"),
      width: 200,
      renderCell: (params) => {
        return (
          <Fragment>
            <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
              {params.row.email}
            </Typography>
          </Fragment>
        );
      },
    },
    {
      field: "dob",
      headerName: Labels.getLabel("startedOnboardings.column.dob", "DOB"),
      width: 80,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.dob}
          </Typography>
        );
      },
    },
    {
      field: "address",
      headerName: Labels.getLabel(
        "startedOnboardings.column.address",
        "Address"
      ),
      width: 300,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.address}
          </Typography>
        );
      },
    },
    {
      field: "phone",
      headerName: Labels.getLabel("startedOnboardings.column.phone", "Phone"),
      width: 100,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.phone}
          </Typography>
        );
      },
    },
    {
      field: "ssn",
      headerName: Labels.getLabel("startedOnboardings.column.ssn", "SSN"),
      width: 90,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.ssn}
          </Typography>
        );
      },
    },
    {
      field: "action",
      width: 100,
      headerName: Labels.getLabel("startedOnboardings.column.action", "Action"),
      renderCell: (params) => {
        return (
          // you will find row info in params
          <div
            style={{
              backgroundColor:
                params.row.status === "REVIEW_IN_PROGRESS"
                  ? "antiquewhite"
                  : "white",
            }}
          >
            <Tooltip
              title={Labels.getLabel(
                "startedOnboardings.resendEmailTooltip",
                "Resend the email to {email}"
              ).replace("{email}", params.row.email)}
            >
              <Button
                style={{ minWidth: "10px" }}
                disabled={resendEmailOnboardingMutation.isLoading}
              >
                <ForwardToInboxIcon
                  onClick={(e) => onResendEmailButtonClick(e, params.row)}
                  variant="contained"
                />
              </Button>
            </Tooltip>

            <Button style={{ minWidth: "10px" }}>
              <DeleteIcon
                style={{ color: "red" }}
                onClick={(e) => onDiscardButtonClick(e, params.row)}
                variant="contained"
              />
            </Button>
            <RenderOnRole roles={["onboarding-super-admin"]}>
              <Button style={{ minWidth: "10px" }}>
                <RemoveCircleOutlineIcon
                  style={{ color: "red" }}
                  onClick={(e) => onRemoveButtonClick(e, params.row)}
                  variant="contained"
                />
              </Button>
            </RenderOnRole>
          </div>
        );
      },
    },
    {
      field: "duration",
      headerName: Labels.getLabel(
        "startedOnboardings.column.duration",
        "Duration"
      ),
      width: 80,
    },

    {
      field: "creationDate",
      headerName: Labels.getLabel(
        "startedOnboardings.column.creationDate",
        "Creation Date"
      ),
      width: 140,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.creationDate}
          </Typography>
        );
      },
    },
    {
      field: "updateDate",
      headerName: Labels.getLabel(
        "startedOnboardings.column.updateDate",
        "Update Date"
      ),
      width: 140,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.updateDate}
          </Typography>
        );
      },
    },
  ];

  const queryClient = useQueryClient();

  const [customerIdentifier, setCustomerIdentifier] = useState();
  const [openEditDlg, setOpenEditDlg] = useState(false);

  const onEditButtonClick = (e, params) => {
    setCustomerIdentifier(params.email);
    setOpenEditDlg(true);
  };

  const handleSuccess = () => {
    refetch();
    setFeedback({
      open: true,
      type: "success",
      message: Labels.getLabel(
        "startedOnboardings.requestSent",
        "The request was sent"
      ),
      onClose: () => {
        //clearFunction();
        setFeedback({ open: false });
        handleClose();
      },
    });
  };

  const discardOnboardingMutation = useMutation(
    (request) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/onboarding/discard`,
        { email: request.email }
      );
    },
    {
      onSuccess: () => {
        handleSuccess();
      },
      onError: (err) => {
        const msg = err?.response?.data?.description
          ? err.response.data.description
          : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const onDiscardButtonClick = (e, params) => {
    discardOnboardingMutation.mutate({ email: params.email });
  };

  const removeOnboardingMutation = useMutation(
    (request) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/onboarding/remove`,
        { email: request.email }
      );
    },
    {
      onSuccess: () => {
        handleSuccess();
      },
      onError: (err) => {
        const msg = err?.response?.data?.description
          ? err.response.data.description
          : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const onRemoveButtonClick = (e, params) => {
    removeOnboardingMutation.mutate({ email: params.email });
  };

  const confirmOnboardingMutation = useMutation(
    (request) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/onboarding/confirm`,
        { email: request.email }
      );
    },
    {
      onSuccess: () => {
        handleSuccess();
      },
      onError: (err) => {
        const msg = err?.response?.data?.description
          ? err.response.data.description
          : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const onConfirmButtonClick = (e, params) => {
    confirmOnboardingMutation.mutate({ email: params.email });
  };

  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
  });

  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedback({ open: false, type: "error", message: "" });
  };

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") return; //avoid closing the modal by clicking the backdrop
    //props.setOpen(false);
  };

  const resendEmailOnboardingMutation = useMutation(
    (request) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/onboarding/resend`,
        { email: request.email }
      );
    },
    {
      onSuccess: () => {
        handleSuccess();
      },
      onError: (err) => {
        const msg = err?.response?.data?.description
          ? err.response.data.description
          : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const onResendEmailButtonClick = (e, params) => {
    resendEmailOnboardingMutation.mutate({ email: params.email });
  };

  async function fetchOnboardingsStarted() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/onboarding/started`
    );
    return response;
  }

  const { data, refetch, isLoading, isError } = useQuery(
    ["onboardings-started"],
    fetchOnboardingsStarted
  );

  return (
    <Container maxWidth="xlg">
      <br />
      <br />
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={12}>
          <h1>
            {Labels.getLabel(
              "startedOnboardings.title",
              "OnBoarding Applications"
            )}
          </h1>

          {isLoading ? (
            <h4>
              {Labels.getLabel("startedOnboardings.loading", "Loading...")}
            </h4>
          ) : !isError ? (
            <div style={{ height: 700, width: "100%" }}>
              <DataGrid
                disableSelectionOnClick
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                rows={data?.data}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10]}
                components={{ Toolbar: GridToolbar }}
                componentsProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
                /* sx={{
                      "& .MuiButton-root": {
                        display: "none",
                      }
                    }}*/
              />
            </div>
          ) : (
            <h4>
              {Labels.getLabel(
                "startedOnboardings.error",
                "An error ocurred, please try again later..."
              )}
            </h4>
          )}
        </Grid>
      </Grid>
      {openEditDlg ? (
        <OnboardingDetail
          action="SAVE"
          open={openEditDlg}
          setOpen={setOpenEditDlg}
          dialogTitle={Labels.getLabel(
            "startedOnboardings.editDialogTitle",
            "Edit Onboarding"
          )}
          customerIdentifier={customerIdentifier}
        />
      ) : (
        ""
      )}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
